// The piles at Sevkiyat: one per carrier and contract, since the same carrier may come once for the
// marketplace's parcels and once for the warehouse's. The packing desk names the pile a parcel goes to.
import { CHANNEL_NAMES, type ShippingModel } from '@wms/domain';
import { carrierLabel } from './format';

interface Parcel {
  readonly channel: string;
  /** an order read before the model was known ships under the warehouse's contract */
  readonly shippingModel?: ShippingModel;
  readonly carrierCode: string | null;
}

/** Whose contract a parcel ships under. */
export function contractOf(o: Pick<Parcel, 'channel' | 'shippingModel'>): string {
  if (o.shippingModel === 'marketplace') return `${CHANNEL_NAMES[o.channel] ?? o.channel} anlaşması`;
  return o.shippingModel === 'seller_contract' ? 'satıcının anlaşması' : 'depo anlaşması';
}

/** "Aras Kargo · Trendyol anlaşması" */
export function pileOf(o: Parcel): string {
  return `${o.carrierCode ? carrierLabel(o.carrierCode) : 'Kargo firması belli değil'} · ${contractOf(o)}`;
}

/** A parcel as a label read at the dock names it. */
interface Labelled {
  readonly orderNumber: string;
  readonly trackingNumber: string | null;
  /** numbers it had before the marketplace moved it to another carrier: their labels are void */
  readonly supersededTracking?: readonly string[];
}

const same = (code: string) => (value: string) => value.toUpperCase() === code;

/** The code read (normalised) is the parcel's label as it is now, or its order number. */
export function labelNow(code: string, o: Labelled): boolean {
  const is = same(code);
  return is(o.orderNumber) || (o.trackingNumber !== null && is(o.trackingNumber));
}

/** The code read is a label printed before the parcel's carrier changed: void, never to the courier. */
export function labelVoid(code: string, o: Labelled): boolean {
  return (o.supersededTracking ?? []).some(same(code));
}

/** The code read is this parcel's: its label now, its order number, or a label printed before a carrier change. */
export const labelOf = (code: string, o: Labelled): boolean => labelNow(code, o) || labelVoid(code, o);

/**
 * KARGOYA VERMEYİN (after the order number) for a label printed before the parcel's carrier changed. A parcel
 * of many boxes has a page of the new label for each box. `handedOver`: the parcel left on its new label and
 * this box still carries an old one, so the new label goes on this box too.
 */
export function voidLabelText(o: Labelled & { readonly carrierCode: string | null; readonly parcelCount?: number }, handedOver = false): string {
  const fresh = `Yeni etiketi (takip no ${o.trackingNumber ?? '—'})`;
  const where = handedOver
    ? 'bu koliye de yapıştırıp okutun'
    : (o.parcelCount ?? 1) > 1
      ? `basıp ${o.parcelCount} kolinin her birinde eski etiketin üstüne yapıştırın ve okutun`
      : 'basıp eskisinin üstüne yapıştırın ve okutun';
  return `Bu etiket geçersiz, kargo firması ${o.carrierCode ? carrierLabel(o.carrierCode) : 'başka bir firma'} olarak değişti. ${fresh} ${where}.`;
}
